import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import accreditationPrintingService from '../../../../services/accreditationPrintingService';
import { renderPdfFirstPage } from './pdfRender';
import TestWithUserModal from './TestWithUserModal';
import {
  QR_FIELD_NAME,
  FONT_FAMILY_OPTIONS,
  getFieldDisplayTitle,
  buildCoordinatesPayload,
  fieldsFromPayload,
} from './templateCoordinate';

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
const r1 = (n) => Math.round(n * 10) / 10;
// The range a text is placed in: Start X .. End X. The dots, the preview, the saved JSON and the PDF all use this
// same value (templateCoordinate.js: endX = x + 180 when none is set), so what you see is what prints.
const rangeEnd = (f) => f.endX ?? Math.round(f.x + (f.width ?? f.boxWidth ?? 180));

const numOr = (v) => {
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) || n <= 0 ? undefined : n;
};

// Same workflow as the digital-certificate "Map Co-ordinates" step: click the template to drop a numbered
// marker (PDF points), drag it, and set what it shows in the rows underneath.
const TemplateDesigner = ({ initial, parentTypes, userTypes, onBack, onSaved }) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [selectedParentType, setSelectedParentType] = useState('');
  const [selectedUserType, setSelectedUserType] = useState('');
  const [fileUrl, setFileUrl] = useState(''); // saved skeleton on the server
  const [fileType, setFileType] = useState('image'); // 'image' | 'pdf'
  const [previewUrl, setPreviewUrl] = useState(''); // what the canvas shows (may be a local blob)
  const [pageDims, setPageDims] = useState({ w: 600, h: 900 }); // PDF points

  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [boundaryDrag, setBoundaryDrag] = useState(null);
  const [resizeDrag, setResizeDrag] = useState(null); // { id, handle: n|s|e|w|ne|nw|se|sw, start: {x,y}, orig: {x,y,w,h} }
  const [previewWidth, setPreviewWidth] = useState(0);
  const [openAdvanced, setOpenAdvanced] = useState({});

  // fields the Add-User form captures (served from the backend, checked against the DB model)
  const [printFields, setPrintFields] = useState([]);
  useEffect(() => {
    accreditationPrintingService
      .getPrintFields()
      .then((res) => setPrintFields(res?.data || []))
      .catch((e) => Swal.fire('Fields not loaded', e.response?.data?.message || e.message, 'error'));
  }, []);

  const [showTestModal, setShowTestModal] = useState(false);

  const previewRef = useRef(null);
  const didDragRef = useRef(false);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const blobRef = useRef(null); // current local preview blob, revoked on replace/unmount

  // Card generation that starts when a template is saved (every "Sent For Printing" user of the type)
  const [genJob, setGenJob] = useState(null);
  const genTimer = useRef(null);
  useEffect(() => () => clearInterval(genTimer.current), []);
  const trackGeneration = (gen) => {
    clearInterval(genTimer.current);
    setGenJob(gen || null);
    if (!gen?.job_id) return;
    genTimer.current = setInterval(async () => {
      try {
        const s = (await accreditationPrintingService.getZipJob(gen.job_id)).data;
        setGenJob(s);
        if (s.status !== 'running') clearInterval(genTimer.current);
      } catch (e) {
        clearInterval(genTimer.current);
        setGenJob({ ...gen, status: 'failed', error: e.response?.data?.message || e.message });
      }
    }, 1500);
  };

  const setPreview = (url, isBlob) => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    blobRef.current = isBlob ? url : null;
    setPreviewUrl(url);
  };
  useEffect(() => () => blobRef.current && URL.revokeObjectURL(blobRef.current), []);

  // Load the template being edited (or reset for a new one)
  useEffect(() => {
    const item = initial || {};
    setTemplateId(item.id || null);
    setTemplateName(item.template_name || '');
    setSelectedParentType(item.parent_type_id ? String(item.parent_type_id) : '');
    setSelectedUserType(item.user_type_id ? String(item.user_type_id) : '');
    setFileUrl(item.file_url || '');
    setFileType(item.file_type || 'image');
    setPageDims({ w: Number(item.canvas_width) || 600, h: Number(item.canvas_height) || 900 });
    let coords = item.coordinates;
    if (typeof coords === 'string') {
      try { coords = JSON.parse(coords); } catch (e) { coords = []; }
    }
    setFields(fieldsFromPayload(coords));
    setSelectedId(null);
    setOpenAdvanced({});
    setPreview('', false);

    if (!item.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (item.file_type === 'pdf') {
          // through the API (not S3 directly) so the browser never hits a CORS wall
          const r = await renderPdfFirstPage(await accreditationPrintingService.getTemplateFile(item.id));
          if (cancelled) return URL.revokeObjectURL(r.url);
          setPreview(r.url, true);
          setPageDims({ w: r.widthPt, h: r.heightPt });
        } else {
          setPreview(item.file_url, false);
        }
      } catch (e) {
        if (!cancelled) Swal.fire('Template not loaded', e.response?.data?.message || e.message, 'warning');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const filteredUserTypes = useMemo(() => {
    if (!selectedParentType) return userTypes;
    return userTypes.filter((u) => Number(u.parent_category_id) === Number(selectedParentType));
  }, [userTypes, selectedParentType]);

  // keep the preview width so marker fonts scale with the picture
  useEffect(() => {
    if (!previewRef.current) return undefined;
    const update = () => {
      const w = previewRef.current?.getBoundingClientRect().width;
      if (w > 0) setPreviewWidth(w);
    };
    update();
    const ob = new ResizeObserver(update);
    ob.observe(previewRef.current);
    return () => ob.disconnect();
  }, [previewUrl]);

  const clientToPdf = (clientX, clientY) => {
    if (!previewRef.current) return { x: 0, y: 0 };
    const rect = previewRef.current.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * pageDims.w,
      y: ((clientY - rect.top) / rect.height) * pageDims.h,
    };
  };

  const clampPosition = (field, x, y) => {
    if (field.type === 'text') {
      const w = Math.max(10, rangeEnd(field) - field.x);
      return { x: clamp(x, 0, Math.max(0, pageDims.w - w)), y: clamp(y, 0, pageDims.h) };
    }
    return {
      x: clamp(x, 0, pageDims.w - (field.imgWidth ?? 250)),
      y: clamp(y, 0, pageDims.h - (field.imgHeight ?? 100)),
    };
  };

  // marker drag
  useEffect(() => {
    if (!dragState) return undefined;
    const move = (e) => {
      didDragRef.current = true;
      const field = fieldsRef.current.find((f) => f.id === dragState.id);
      if (!field) return;
      const p = clientToPdf(e.clientX, e.clientY);
      const next = clampPosition(field, p.x - dragState.offsetX, p.y - dragState.offsetY);
      const dx = next.x - field.x;
      const dy = next.y - field.y;
      setFields((prev) => prev.map((f) => {
        if (f.id !== dragState.id) return f;
        if (f.type !== 'text') return { ...f, x: r1(next.x), y: r1(next.y) };
        return {
          ...f,
          x: r1(next.x),
          y: r1(next.y),
          endX: r1(rangeEnd(f) + dx), // the whole range moves, its width stays
          ...(f.row2X != null ? { row2X: r1(f.row2X + dx) } : {}),
          ...(f.row2EndX != null ? { row2EndX: r1(f.row2EndX + dx) } : {}),
          ...(f.row2Y != null ? { row2Y: r1(f.row2Y + dy) } : {}),
        };
      }));
    };
    const up = () => setDragState(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, pageDims]);

  // image / QR box resize: drag any edge or corner (hold Shift on a corner to keep the proportions)
  useEffect(() => {
    if (!resizeDrag) return undefined;
    const MIN = 10; // pt
    const move = (e) => {
      didDragRef.current = true;
      const p = clientToPdf(e.clientX, e.clientY);
      const dx = p.x - resizeDrag.start.x;
      const dy = p.y - resizeDrag.start.y;
      const { handle, orig } = resizeDrag;
      let { x, y, w, h } = orig;
      if (handle.includes('e')) w = orig.w + dx;
      if (handle.includes('s')) h = orig.h + dy;
      if (handle.includes('w')) { w = orig.w - dx; x = orig.x + dx; }
      if (handle.includes('n')) { h = orig.h - dy; y = orig.y + dy; }

      if (e.shiftKey && handle.length === 2) { // corner + Shift: keep the aspect ratio
        const k = Math.max(w / orig.w, h / orig.h);
        w = orig.w * k;
        h = orig.h * k;
        if (handle.includes('w')) x = orig.x + orig.w - w;
        if (handle.includes('n')) y = orig.y + orig.h - h;
      }

      // never smaller than MIN: the edge opposite the one being dragged stays put
      if (w < MIN) { if (handle.includes('w')) x = orig.x + orig.w - MIN; w = MIN; }
      if (h < MIN) { if (handle.includes('n')) y = orig.y + orig.h - MIN; h = MIN; }
      // stay inside the page
      if (x < 0) { w += x; x = 0; }
      if (y < 0) { h += y; y = 0; }
      if (x + w > pageDims.w) w = pageDims.w - x;
      if (y + h > pageDims.h) h = pageDims.h - y;

      setFields((prev) => prev.map((f) => (f.id === resizeDrag.id ? { ...f, x: r1(x), y: r1(y), imgWidth: r1(w), imgHeight: r1(h) } : f)));
    };
    const up = () => setResizeDrag(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeDrag, pageDims]);

  // boundary dot drag (start / end / row 2 start / row 2 end)
  useEffect(() => {
    if (!boundaryDrag) return undefined;
    const move = (e) => {
      didDragRef.current = true;
      const p = clientToPdf(e.clientX, e.clientY);
      const cx = Math.round(clamp(p.x, 0, pageDims.w));
      const cy = Math.round(clamp(p.y, 0, pageDims.h));
      setFields((prev) => prev.map((f) => {
        if (f.id !== boundaryDrag.fieldId) return f;
        const endX = rangeEnd(f);
        switch (boundaryDrag.handle) {
          case 'startX': return { ...f, x: Math.min(cx, endX - 10), y: cy, endX }; // End X stays where it is
          case 'endX': return { ...f, endX: Math.max(cx, f.x + 10) };
          case 'row2X': return { ...f, row2X: cx, row2Y: cy, row2EndX: f.row2EndX ?? endX };
          case 'row2EndX': return { ...f, row2EndX: cx, row2Y: cy, row2X: f.row2X ?? f.x };
          default: return f;
        }
      }));
    };
    const up = () => setBoundaryDrag(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryDrag, pageDims]);

  // ---- upload ------------------------------------------------------------------------------------------
  const handleFileUpload = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;

    const isPdf = picked.type === 'application/pdf' || picked.name.toLowerCase().endsWith('.pdf');
    if (!isPdf && !picked.type.startsWith('image/')) {
      Swal.fire('Invalid File', 'Please upload a PDF or an image (PNG, JPG, JPEG) as the card template.', 'error');
      return;
    }

    setUploading(true);
    try {
      if (isPdf) {
        const r = await renderPdfFirstPage(picked);
        setPreview(r.url, true);
        setPageDims({ w: r.widthPt, h: r.heightPt });
      } else {
        const url = URL.createObjectURL(picked);
        const dims = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.width, h: img.height });
          img.onerror = () => reject(new Error('Could not read the image'));
          img.src = url;
        });
        setPreview(url, true);
        setPageDims({ w: 600, h: Math.round((dims.h / dims.w) * 600) }); // image template: 600pt wide page
      }
      setFileUrl('');
      const res = await accreditationPrintingService.uploadTemplateMedia(picked);
      if (!(res?.success && res?.data?.fileUrl)) throw new Error(res?.message || 'Upload failed');
      setFileUrl(res.data.fileUrl);
      setFileType(res.data.fileType);
    } catch (error) {
      console.error('File upload error:', error);
      setFileUrl(''); // never keep a local blob: URL, it cannot be saved or rendered server-side
      Swal.fire('Upload Failed', error.response?.data?.message || error.message, 'warning');
    } finally {
      setUploading(false);
    }
  };

  // ---- fields --------------------------------------------------------------------------------------------
  const handlePreviewClick = (event) => {
    if (!previewUrl || didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const { x, y } = clientToPdf(event.clientX, event.clientY);
    const number = fields.length + 1;
    const endX = r1(Math.max(x + 30, Math.min(x + 180, pageDims.w - 5)));
    const field = { id: `f_${Date.now()}`, number, x: r1(x), y: r1(y), endX, type: 'text', title: '', fontSize: '14', fontFamily: 'Helvetica', textAlign: 'center' };
    setFields((prev) => [...prev, field]);
    setSelectedId(field.id);
  };

  const handleMarkerMouseDown = (event, field) => {
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
    setSelectedId(field.id);
    const p = clientToPdf(event.clientX, event.clientY);
    setDragState({ id: field.id, offsetX: p.x - field.x, offsetY: p.y - field.y });
  };

  const handleResizeMouseDown = (event, field, handle) => {
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
    setSelectedId(field.id);
    setResizeDrag({
      id: field.id,
      handle,
      start: clientToPdf(event.clientX, event.clientY),
      orig: { x: field.x, y: field.y, w: field.imgWidth ?? 250, h: field.imgHeight ?? 100 },
    });
  };

  const handleBoundaryMouseDown = (event, fieldId, handle) => {
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
    setSelectedId(fieldId);
    setBoundaryDrag({ fieldId, handle });
  };

  const updateField = (id, patch) => setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  // switching Text <-> Image keeps the chosen field only if it is valid for the new type
  const keepTitleFor = (field, type) => (printFields.some((p) => p.key === field.title && p.type === type) ? field.title : '');

  // choosing a field also sets the marker type (e.g. Photo turns a text marker into an image box)
  // A new image / QR box is a square sized to the page (about 35% / 22% of its short side), placed fully
  // inside it, so every edge and corner is reachable. Resize it by dragging, or type Width / Height.
  const boxFor = (field, kind) => {
    const s = Math.max(24, Math.round(Math.min(pageDims.w, pageDims.h) * (kind === 'qr' ? 0.22 : 0.35)));
    return {
      imgWidth: field.imgWidth ?? s,
      imgHeight: field.imgHeight ?? s,
      x: clamp(field.x, 0, Math.max(0, pageDims.w - (field.imgWidth ?? s))),
      y: clamp(field.y, 0, Math.max(0, pageDims.h - (field.imgHeight ?? s))),
    };
  };

  const pickField = (field, key) => {
    const pf = printFields.find((p) => p.key === key);
    if (!pf || pf.type === field.type) return updateField(field.id, { title: key });
    if (pf.type === 'image') updateField(field.id, { type: 'image', title: key, ...boxFor(field, 'image') });
    else updateField(field.id, { type: 'text', title: key, fontSize: field.fontSize ?? '14', fontFamily: field.fontFamily ?? 'Helvetica' });
  };

  const changeType = (field, type) => {
    if (type === 'text') {
      updateField(field.id, { type, title: keepTitleFor(field, type), fontSize: field.fontSize ?? '14', fontFamily: field.fontFamily ?? 'Helvetica' });
    } else if (type === 'qr') {
      updateField(field.id, { type, title: QR_FIELD_NAME, ...boxFor(field, 'qr') });
    } else {
      updateField(field.id, { type, title: keepTitleFor(field, type), ...boxFor(field, 'image') });
    }
  };

  const deleteField = (id) => {
    if (selectedId === id) setSelectedId(null);
    setFields((prev) => prev
      .filter((f) => f.id !== id)
      .map((f, i) => ({ ...f, number: i + 1 })));
  };

  const fieldErrors = useMemo(() => {
    const counts = {};
    fields.forEach((f) => {
      const t = (f.type === 'qr' ? QR_FIELD_NAME : f.title).trim().toLowerCase();
      if (t) counts[t] = (counts[t] || 0) + 1;
    });
    const errors = {};
    fields.forEach((f) => {
      if (f.type === 'qr') return;
      const t = f.title.trim().toLowerCase();
      if (!t) errors[f.id] = 'Select a field';
      else if (counts[t] > 1) errors[f.id] = 'Field name must be unique';
    });
    return errors;
  }, [fields]);

  // ---- save ----------------------------------------------------------------------------------------------
  const handleSaveTemplate = async () => {
    if (!selectedParentType) return Swal.fire('Required Field', 'Please select a Parent Type.', 'warning');
    if (!selectedUserType) return Swal.fire('Required Field', 'Please select a User Type.', 'warning');
    if (!fileUrl || fileUrl.startsWith('blob:') || uploading) {
      return Swal.fire('Template Required', 'Please upload the card template and wait for the upload to finish.', 'warning');
    }
    if (fields.length === 0) return Swal.fire('Coordinates Required', 'Click on the template to mark at least one field.', 'warning');
    if (Object.keys(fieldErrors).length) return Swal.fire('Fix Field Names', 'Every marker needs a unique field selected from the list.', 'warning');

    setSaving(true);
    try {
      const parentName = parentTypes.find((p) => p.category_id === Number(selectedParentType))?.category_name_view || '';
      const userName = userTypes.find((u) => u.sub_category_id === Number(selectedUserType))?.sub_category_name_view || '';
      const autoName = templateName.trim() || `${parentName} - ${userName} Template`;

      const res = await accreditationPrintingService.saveTemplate({
        id: templateId || undefined,
        template_name: autoName,
        parent_type_id: Number(selectedParentType),
        user_type_id: Number(selectedUserType),
        file_url: fileUrl,
        file_type: fileType,
        canvas_width: pageDims.w, // PDF points
        canvas_height: pageDims.h,
        coordinates: buildCoordinatesPayload(fields),
        status: 'active',
      });
      if (!res?.success) throw new Error(res?.message || 'Failed to save');
      setTemplateId(res.data?.id || templateId);
      const gen = res.data?.generation;
      Swal.fire({
        icon: 'success',
        title: 'Saved Successfully!',
        text: gen?.job_id ? `Template "${autoName}" saved. Generating cards for ${gen.total} user(s) in "Sent For Printing"…` : `Template "${autoName}" has been stored.`,
        timer: 2200,
        showConfirmButton: false,
      });
      trackGeneration(gen);
      onSaved?.(res.data);
    } catch (error) {
      console.error('Save template error:', error);
      Swal.fire('Save Error', error.response?.data?.message || error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- rendering helpers ----------------------------------------------------------------------------------
  const scale = previewWidth > 0 ? previewWidth / pageDims.w : 0.8;
  const pct = (v, total) => `${(v / total) * 100}%`;
  const selectedField = fields.find((f) => f.id === selectedId) || null;

  const renderDot = (key, x, y, color, label, fieldId, handle) => (
    <div
      key={key}
      onMouseDown={(e) => handleBoundaryMouseDown(e, fieldId, handle)}
      title={`${label} (${Math.round(x)})`}
      style={{ position: 'absolute', left: pct(x, pageDims.w), top: pct(y, pageDims.h), zIndex: 30, padding: 8, transform: 'translate(-50%, -50%)', cursor: 'grab', lineHeight: 'normal', touchAction: 'none' }}
    >
      <div style={{ width: 14, height: 14, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,.4)', pointerEvents: 'none' }} />
    </div>
  );

  const renderLine = (key, x1, x2, y, color) => (
    <div key={key} style={{ position: 'absolute', left: pct(Math.min(x1, x2), pageDims.w), top: pct(y, pageDims.h), width: pct(Math.abs(x2 - x1), pageDims.w), borderTop: `2px dashed ${color}`, pointerEvents: 'none', zIndex: 15 }} />
  );

  const renderBoundaries = () => {
    if (!selectedField || selectedField.type !== 'text') return null;
    const f = selectedField;
    const endX = rangeEnd(f);
    const out = [
      renderDot('s', f.x, f.y, '#10b981', 'Start', f.id, 'startX'),
      renderDot('e', endX, f.y, '#ef4444', 'End', f.id, 'endX'),
      renderLine('l1', f.x, endX, f.y, 'rgba(239,68,68,.5)'),
    ];
    if ((f.maxLines ?? 1) > 1) {
      const size = Number(String(f.fontSize ?? 14).replace(/[^0-9.]/g, '')) || 14;
      const row2Y = f.row2Y ?? f.y + size * 1.15;
      const r2X = f.row2X ?? f.x;
      const r2End = f.row2EndX ?? endX;
      out.push(
        renderDot('r2s', r2X, row2Y, '#3b82f6', 'Row2 Start', f.id, 'row2X'),
        renderDot('r2e', r2End, row2Y, '#f97316', 'Row2 End', f.id, 'row2EndX'),
        renderLine('l2', r2X, r2End, row2Y, 'rgba(249,115,22,.5)')
      );
    }
    return out;
  };

  const numInput = (label, value, onChange, placeholder = 'auto', width = 90) => (
    <div style={{ width }}>
      <label className="form-label small mb-1">{label}</label>
      <input className="form-control form-control-sm" placeholder={placeholder} value={value != null ? String(Math.round(value)) : ''} onChange={(e) => onChange(numOr(e.target.value))} />
    </div>
  );

  return (
    <div>
      {genJob && (
        <div className={`alert py-2 small ${genJob.status === 'failed' ? 'alert-danger' : genJob.status === 'running' ? 'alert-info' : 'alert-success'}`} data-testid="generation-status">
          {genJob.status === 'running' && (
            <div className="d-flex align-items-center gap-3">
              <Spinner size="sm" animation="border" />
              <span>Generating cards for users in <b>Sent For Printing</b>: {genJob.done}/{genJob.total}</span>
              <div className="progress flex-grow-1" style={{ maxWidth: 260, height: 10 }}>
                <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${genJob.total ? Math.round((genJob.done / genJob.total) * 100) : 0}%` }} />
              </div>
            </div>
          )}
          {genJob.status === 'done' && genJob.total === 0 && <span>No users are in <b>Sent For Printing</b> for this parent / user type yet, so no cards were generated.</span>}
          {genJob.status === 'done' && genJob.total > 0 && (
            <span><b>{genJob.generated}</b> card(s) generated{genJob.skipped ? <>, <b>{genJob.skipped}</b> failed</> : null} (users in Sent For Printing).</span>
          )}
          {genJob.status === 'cancelled' && <span>Generation was replaced by a newer save.</span>}
          {genJob.status === 'failed' && <span>Card generation failed: {genJob.error || 'see server log'}</span>}
        </div>
      )}
      <div className="d-flex flex-wrap gap-2 justify-content-end mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
          <i className="fa fa-arrow-left me-1" /> Back to Templates
        </button>
        {templateId && selectedUserType && (
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() => setShowTestModal(true)}
            title="Render this saved template with a real user"
          >
            <i className="fa fa-user me-1" /> Test with real user
          </button>
        )}
        <button className="btn btn-primary btn-sm px-3" onClick={handleSaveTemplate} disabled={saving || uploading}>
          {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : <><i className="fa fa-save me-1" /> Save Template</>}
        </button>
      </div>

      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-bold mb-1">Template Name</label>
              <input type="text" className="form-control form-control-sm" placeholder="e.g. Broadcast Accreditation Card" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold mb-1">Parent Type <span className="text-danger">*</span></label>
              <select className="form-select form-select-sm" value={selectedParentType} onChange={(e) => { setSelectedParentType(e.target.value); setSelectedUserType(''); }}>
                <option value="">-- Select Parent Type --</option>
                {parentTypes.map((p) => <option key={p.category_id} value={p.category_id}>{p.category_name_view}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold mb-1">User Type <span className="text-danger">*</span></label>
              <select className="form-select form-select-sm" value={selectedUserType} onChange={(e) => setSelectedUserType(e.target.value)}>
                <option value="">-- Select User Type --</option>
                {filteredUserTypes.map((u) => <option key={u.sub_category_id} value={u.sub_category_id}>{u.sub_category_name_view}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold mb-1">Card Template (PDF or Image) <span className="text-danger">*</span></label>
              <div className="input-group input-group-sm">
                <input type="file" className="form-control form-control-sm" accept="application/pdf,image/png,image/jpeg,image/jpg" onChange={handleFileUpload} disabled={uploading} />
                {uploading && <span className="input-group-text bg-light"><Spinner size="sm" animation="border" /></span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-white py-3">
          <h5 className="mb-1 fw-bold">Map Co-ordinates</h5>
          <small className="text-muted">
            Click on the card preview to mark where dynamic content goes (name, photo, QR…). Each click creates a numbered marker. Drag a marker to adjust it, then set what it shows below. Positions are PDF points ({Math.round(pageDims.w)} × {Math.round(pageDims.h)} pt page).
          </small>
        </div>
        <div className="card-body">
          <div className="mx-auto p-3 rounded-3 border bg-light" style={{ maxWidth: 720, cursor: previewUrl ? 'crosshair' : 'not-allowed' }}>
            <div ref={previewRef} onClick={handlePreviewClick} className="position-relative w-100 overflow-hidden rounded-2" style={{ lineHeight: 0, zIndex: 0, isolation: 'isolate' }}>
              {previewUrl ? (
                <img src={previewUrl} alt="Card template preview" className="d-block w-100" style={{ height: 'auto', pointerEvents: 'none' }} draggable={false} />
              ) : (
                <div className="d-flex align-items-center justify-content-center text-muted text-center px-4" style={{ minHeight: 320, lineHeight: 'normal' }}>
                  {uploading ? <Spinner animation="border" /> : 'Upload a blank card template (PDF or image) to start mapping coordinates.'}
                </div>
              )}

              {fields.map((field) => {
                const selected = selectedId === field.id;
                const label = printFields.find((p) => p.key === field.title)?.label ?? getFieldDisplayTitle(field);
                const common = {
                  onMouseDown: (e) => handleMarkerMouseDown(e, field),
                  onClick: (e) => e.stopPropagation(),
                };
                if (field.type === 'text') {
                  const size = Number(String(field.fontSize ?? 14).replace(/[^0-9.]/g, '')) || 14;
                  const start = field.x;
                  const end = rangeEnd(field);
                  const align = field.textAlign || 'center';
                  const shortLabel = String(label).split(' (')[0];
                  return (
                    <div
                      key={field.id}
                      {...common}
                      title={`${shortLabel}: ${Math.round(start)} to ${Math.round(end)} pt, ${align}-aligned`}
                      style={{
                        position: 'absolute', left: pct(start, pageDims.w), width: pct(Math.max(10, end - start), pageDims.w), top: pct(field.y, pageDims.h),
                        zIndex: selected ? 25 : 10, transform: 'translateY(-85%)',
                        display: 'flex', justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center', // centres even when the text is wider than the range
                        whiteSpace: 'nowrap', cursor: 'move', userSelect: 'none', touchAction: 'none',
                        fontSize: `${Math.max(8, size * scale)}px`, fontFamily: field.fontFamily || 'Helvetica', fontWeight: 600, lineHeight: 'normal',
                        color: selected ? '#4f46e5' : '#7c3aed', textShadow: '0 0 3px #fff',
                        border: `1px dashed ${selected ? '#6366f1' : 'rgba(99,102,241,.35)'}`, background: selected ? 'rgba(99,102,241,.08)' : 'transparent', borderRadius: 2,
                      }}
                    >
                      <span style={{ flex: '0 0 auto', pointerEvents: 'none' }}>{shortLabel}</span>
                    </div>
                  );
                }
                const w = field.imgWidth ?? 250;
                const h = field.imgHeight ?? 100;
                return (
                  <div
                    key={field.id}
                    {...common}
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      position: 'absolute', left: pct(field.x, pageDims.w), top: pct(field.y, pageDims.h), width: pct(w, pageDims.w), height: pct(h, pageDims.h),
                      minWidth: 6, minHeight: 6, zIndex: selected ? 20 : 10, cursor: 'move', border: '2px dashed #7c3aed', background: selected ? 'rgba(124,58,237,.2)' : 'rgba(255,255,255,.8)',
                      boxShadow: selected ? '0 0 0 2px rgba(124,58,237,.4)' : 'none', userSelect: 'none', touchAction: 'none', lineHeight: 'normal',
                    }}
                  >
                    <span style={{ pointerEvents: 'none', fontSize: 10, fontWeight: 600, color: '#7c3aed', textAlign: 'center', overflow: 'hidden' }}>
                      {label}
                      {selected && <><br />{Math.round(w)} × {Math.round(h)} pt</>}
                    </span>
                    {/* resize: grab anywhere along an edge, or a corner */}
                    {[
                      ['n', { left: 0, right: 0, top: -4, height: 8, cursor: 'ns-resize' }],
                      ['s', { left: 0, right: 0, bottom: -4, height: 8, cursor: 'ns-resize' }],
                      ['w', { top: 0, bottom: 0, left: -4, width: 8, cursor: 'ew-resize' }],
                      ['e', { top: 0, bottom: 0, right: -4, width: 8, cursor: 'ew-resize' }],
                    ].map(([hd, st]) => (
                      <div key={hd} data-handle={hd} onMouseDown={(e) => handleResizeMouseDown(e, field, hd)} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', zIndex: 1, ...st }} />
                    ))}
                    {selected && [
                      ['nw', { left: 0, top: 0, cursor: 'nwse-resize' }],
                      ['n', { left: '50%', top: 0, cursor: 'ns-resize' }],
                      ['ne', { left: '100%', top: 0, cursor: 'nesw-resize' }],
                      ['e', { left: '100%', top: '50%', cursor: 'ew-resize' }],
                      ['se', { left: '100%', top: '100%', cursor: 'nwse-resize' }],
                      ['s', { left: '50%', top: '100%', cursor: 'ns-resize' }],
                      ['sw', { left: 0, top: '100%', cursor: 'nesw-resize' }],
                      ['w', { left: 0, top: '50%', cursor: 'ew-resize' }],
                    ].map(([hd, st]) => (
                      <div
                        key={`knob-${hd}`}
                        data-knob={hd}
                        onMouseDown={(e) => handleResizeMouseDown(e, field, hd)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'absolute', zIndex: 2, width: 11, height: 11, background: '#fff', border: '2px solid #7c3aed', borderRadius: 2, transform: 'translate(-50%, -50%)', ...st }}
                      />
                    ))}
                    {/* corners are also grabbable when the box is not selected yet */}
                    {!selected && ['nw', 'ne', 'se', 'sw'].map((hd) => (
                      <div
                        key={`corner-${hd}`}
                        data-handle={hd}
                        onMouseDown={(e) => handleResizeMouseDown(e, field, hd)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'absolute', zIndex: 2, width: 12, height: 12, transform: 'translate(-50%, -50%)', cursor: hd === 'nw' || hd === 'se' ? 'nwse-resize' : 'nesw-resize', left: hd.includes('w') ? 0 : '100%', top: hd.includes('n') ? 0 : '100%' }}
                      />
                    ))}
                  </div>
                );
              })}
              {renderBoundaries()}
            </div>
          </div>
        </div>
      </div>

      {fields.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body py-0">
            {fields.map((field) => {
              const selected = selectedId === field.id;
              const adv = !!openAdvanced[field.id];
              const err = fieldErrors[field.id];
              return (
                <div key={field.id} onClick={() => setSelectedId(field.id)} className={`py-3 border-bottom ${selected ? 'bg-light rounded px-2' : ''}`} style={{ cursor: 'pointer' }}>
                  <div className="d-flex flex-wrap align-items-start gap-3">
                    <div className="d-flex align-items-center justify-content-center border rounded bg-white fw-semibold" style={{ width: 40, height: 40 }}>{field.number}</div>

                    <div>
                      <label className="form-label small fw-semibold mb-1">Field Type</label>
                      <div className="d-flex gap-3">
                        {[['text', 'Text'], ['image', 'Image'], ['qr', 'QR']].map(([v, l]) => (
                          <div className="form-check" key={v}>
                            <input className="form-check-input" type="radio" name={`type-${field.id}`} id={`type-${field.id}-${v}`} checked={field.type === v} onChange={() => changeType(field, v)} />
                            <label className="form-check-label small" htmlFor={`type-${field.id}-${v}`}>{l}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ minWidth: 200, flex: 1 }}>
                      <label className="form-label small fw-semibold mb-1">Field Name <span className="text-danger">*</span></label>
                      {field.type === 'qr' ? (
                        <input className="form-control form-control-sm" value={QR_FIELD_NAME} disabled readOnly />
                      ) : (
                        <select
                          className={`form-select form-select-sm ${err ? 'is-invalid' : ''}`}
                          value={field.title}
                          onChange={(e) => pickField(field, e.target.value)}
                        >
                          <option value="">-- Select a field from the user form --</option>
                          {[['text', 'Text fields'], ['image', 'Images (profile image, ID document)']].map(([t, group]) => (
                            <optgroup key={t} label={group}>
                              {printFields.filter((p) => p.type === t).map((p) => (
                                <option key={p.key} value={p.key}>{p.label} ({p.key})</option>
                              ))}
                            </optgroup>
                          ))}
                          {field.title && !printFields.some((p) => p.key === field.title) && (
                            <option value={field.title}>{field.title} (not a user-form field)</option>
                          )}
                        </select>
                      )}
                      {err && <div className="invalid-feedback">{err}</div>}
                    </div>

                    {field.type === 'text' ? (
                      <>
                        <div style={{ width: 100 }}>
                          <label className="form-label small fw-semibold mb-1">Font Size</label>
                          <input className="form-control form-control-sm" placeholder="14" value={String(field.fontSize ?? '')} onChange={(e) => updateField(field.id, { fontSize: e.target.value.replace(/[^0-9]/g, '') })} />
                        </div>
                        <div style={{ width: 170 }}>
                          <label className="form-label small fw-semibold mb-1">Font Family</label>
                          <select className="form-select form-select-sm" value={field.fontFamily ?? 'Helvetica'} onChange={(e) => updateField(field.id, { fontFamily: e.target.value })}>
                            {FONT_FAMILY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ width: 100 }}>
                          <label className="form-label small fw-semibold mb-1">Height</label>
                          <input className="form-control form-control-sm" placeholder="40" value={field.imgHeight ? String(field.imgHeight) : ''} onChange={(e) => updateField(field.id, { imgHeight: Number(e.target.value.replace(/[^0-9.]/g, '')) || 0 })} />
                        </div>
                        <div style={{ width: 100 }}>
                          <label className="form-label small fw-semibold mb-1">Width</label>
                          <input className="form-control form-control-sm" placeholder="40" value={field.imgWidth ? String(field.imgWidth) : ''} onChange={(e) => updateField(field.id, { imgWidth: Number(e.target.value.replace(/[^0-9.]/g, '')) || 0 })} />
                        </div>
                      </>
                    )}

                    <div className="align-self-end">
                      <button className="btn btn-outline-danger btn-sm" title="Delete field" onClick={(e) => { e.stopPropagation(); deleteField(field.id); }}>
                        <i className="fa fa-trash" />
                      </button>
                    </div>
                  </div>

                  {field.type === 'text' && (
                    <div className="ms-5 mt-2">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenAdvanced((o) => ({ ...o, [field.id]: !adv }));
                          if (!adv && field.endX == null) updateField(field.id, { endX: rangeEnd(field) });
                        }}
                      >
                        <i className={`fa fa-chevron-${adv ? 'down' : 'right'} me-1`} /> Text Overflow Settings
                      </button>
                      {adv && (
                        <div className="mt-2 p-3 border rounded bg-light d-flex flex-column gap-3" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex flex-wrap gap-3">
                            {numInput('Max Lines', field.maxLines, (v) => updateField(field.id, { maxLines: v ?? 1 }), '1', 90)}
                            {numInput('Shrink to fit (min pt)', field.minFontSize, (v) => updateField(field.id, { minFontSize: v }), 'off', 150)}
                            <div style={{ width: 110 }}>
                              <label className="form-label small mb-1">Align</label>
                              <select className="form-select form-select-sm" value={field.textAlign ?? 'center'} onChange={(e) => updateField(field.id, { textAlign: e.target.value })}>
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <div className="small text-muted fw-semibold mb-1">Row 1 Boundaries (PDF points)</div>
                            <div className="d-flex flex-wrap gap-3">
                              {numInput('Start X', field.x, (v) => v !== undefined && updateField(field.id, { x: v }))}
                              {numInput('Start Y', field.y, (v) => v !== undefined && updateField(field.id, { y: v }))}
                              {numInput('End X', field.endX, (v) => updateField(field.id, { endX: v }))}
                            </div>
                          </div>
                          {(field.maxLines ?? 1) > 1 && (
                            <div>
                              <div className="small text-muted fw-semibold mb-1">Row 2 Boundaries (for multi-line overflow)</div>
                              <div className="d-flex flex-wrap gap-3">
                                {numInput('Row 2 X', field.row2X, (v) => updateField(field.id, { row2X: v }), 'same')}
                                {numInput('Row 2 End X', field.row2EndX, (v) => updateField(field.id, { row2EndX: v }), 'same', 110)}
                                {numInput('Row 2 Y', field.row2Y, (v) => updateField(field.id, { row2Y: v }))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <TestWithUserModal show={showTestModal} onHide={() => setShowTestModal(false)} templateId={templateId} userTypeId={selectedUserType} />
    </div>
  );
}

export default TemplateDesigner;